'use client';

import { useEffect, useMemo } from 'react';
import { ReactComponent as Cake } from '@assets/icons/cake.svg';
import { ReactComponent as CircleQuestion } from '@assets/icons/circle-question.svg';
import { ReactComponent as Envelope } from '@assets/icons/envelope.svg';
import { ReactComponent as Target } from '@assets/icons/location-dot.svg';
import { ReactComponent as GenderIcon } from '@assets/icons/person-half-dress.svg';
import { ReactComponent as Phone } from '@assets/icons/phone.svg';
import { ReactComponent as User } from '@assets/icons/user.svg';
import { ReactComponent as UserGroup } from '@assets/icons/user-group.svg';
import { checkEmail, checkRefCode } from 'actions/auth';
import config from 'config';
import { createAccountSchema, ROUTES, zipCodeMask } from 'constant';
import { useFormik } from 'formik';
import { useRouter } from 'next/navigation';

import {
  Box,
  Center,
  Checkbox,
  Flex,
  SimpleGrid,
  Stack,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearErrors, setRefCodeData } from 'store/reducers/userSlice';

import { FormType, ReactSelectItemInterface } from 'types';

import { DatePicker, IconTextField, Link, SelectField, Text } from 'ui';
import { AuthStepButtons } from '../../../AuthStepButtons';

const {
  login: { isPolicyAcceptRequired, genderValues, genderPlaceholder }
} = config;

type AccountFormProps = {
  formState: FormType;
  referalCode?: string;
  onSubmit: (values: FormType) => void;
  isReferalFlow?: boolean;
  isBackButtonEnabled?: boolean;
};

const AccountForm = ({
  formState,
  referalCode = '',
  onSubmit,
  isReferalFlow = false,
  isBackButtonEnabled = false
}: AccountFormProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const fontSize = useBreakpointValue({ base: 16, sm: 18, md: 19, xl: 22 });
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ base: true, lg: false });
  const { errorMsg, fieldErrors, userCampaigns, refCodeData } = useAppSelector(
    (state) => state.user
  );
  const {
    values,
    errors,
    setFieldValue,
    setFieldTouched,
    validateField,
    isValid,
    touched,
    setErrors,
    handleSubmit,
    getFieldProps
  } = useFormik({
    initialValues: formState,
    validationSchema: createAccountSchema(
      isReferalFlow,
      isPolicyAcceptRequired
    ),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (formData: FormType) => {
      const onResetFlow = () => router.push(ROUTES.forgotPassword);
      const isSuccess = await dispatch(
        checkEmail({
          email: formData.email,
          bday: formData.bday_date,
          onResetFlow
        })
      );

      if (isSuccess) {
        onSubmit(formData);
      }

      if (!isReferalFlow) {
        if (formData.ref_code) {
          dispatch(checkRefCode(formData.ref_code));
        } else if (refCodeData !== null) {
          dispatch(setRefCodeData(null));
        }
      }
    }
  });

  const {
    fonts,
    colors: {
      palette: {
        auth: {
          createAccount: { accountForm: palette }
        }
      }
    }
  } = useTheme();

  const isErrorsExist = Object.entries(fieldErrors).length > 0;
  const isReferalCodeExist = Boolean(referalCode);

  const hasPrefilledData = Boolean(
    formState.email ||
      formState.givname ||
      formState.surname ||
      formState.ph_mobile ||
      (!isReferalFlow && formState.postal)
  );

  const getAutoFocusField = () => {
    if (!hasPrefilledData) return 'givname';

    if (!values.givname) return 'givname';
    if (!values.surname) return 'surname';
    if (!values.email) return 'email';
    if (!values.ph_mobile) return 'ph_mobile';
    if (!isReferalFlow && !values.postal) return 'postal';
    if (!values.bday_date) return 'bday_date';
    if (!values.ref_code && !referalCode) return 'ref_code';

    return null;
  };

  const autoFocusField = getAutoFocusField();

  const getError = (fieldName: keyof FormType): string | undefined => {
    const errorFromFormik = errors[fieldName];
    const errorFromFieldErrors = fieldErrors[fieldName];
    return errorFromFieldErrors || errorFromFormik;
  };

  const handleClearErrors = () => {
    if (errorMsg || isErrorsExist || !isValid) {
      setErrors({});
      dispatch(clearErrors());
    }
  };

  const handleDateChange = (value: string) => {
    setFieldValue('bday_date', value);
    handleClearErrors();
  };

  const handleInputChange = (name: string) => (value: string) => {
    setFieldValue(name, value);
    handleClearErrors();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => handleClearErrors(), []);

  useEffect(() => {
    if (referalCode) {
      setFieldValue('ref_code', referalCode);
    }
  }, [referalCode, setFieldValue]);

  const campaignsOptions: ReactSelectItemInterface[] = useMemo(
    () =>
      (userCampaigns ?? []).map(({ name, id }) => ({
        label: name,
        value: String(id)
      })),
    [userCampaigns]
  );

  const onLoginPage = () => router.push(ROUTES.login);

  return (
    <Box
      as='form'
      onSubmit={handleSubmit}
      width='100%'
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
    >
      <Box alignItems='center' w='full'>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing='10px'>
          <IconTextField
            autoFocus={autoFocusField === 'givname'}
            pb={{ base: 0.5, md: 1 }}
            {...getFieldProps('givname')}
            placeholder='First name'
            leftIcon={
              <User
                fill={palette.iconColor}
                width={22}
                height={27}
                opacity={0.6}
              />
            }
            onChange={handleInputChange('givname')}
            iconBg={palette.iconBg}
            error={getError('givname')}
          />
          <IconTextField
            autoFocus={autoFocusField === 'surname'}
            pb={{ base: 0.5, md: 1 }}
            {...getFieldProps('surname')}
            placeholder='Last name'
            onChange={handleInputChange('surname')}
            iconBg={palette.iconBg}
            error={getError('surname')}
            leftIcon={
              isMobile ? (
                <User
                  fill={palette.iconColor}
                  width={22}
                  height={27}
                  opacity={0.6}
                />
              ) : null
            }
            m={{
              md: 0
            }}
          />
        </SimpleGrid>
        <Box my='10px'>
          <IconTextField
            autoFocus={autoFocusField === 'email'}
            pb={{ base: 0.5, md: 1 }}
            {...getFieldProps('email')}
            placeholder='Email'
            iconBg={palette.iconBg}
            onChange={handleInputChange('email')}
            error={getError('email')}
            leftIcon={
              <Envelope
                width={24}
                height={24}
                fill={palette.iconColor}
                opacity={0.6}
              />
            }
          />
        </Box>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing='10px'>
          <IconTextField
            autoFocus={autoFocusField === 'ph_mobile'}
            pb={{ base: 0.5, md: 1 }}
            {...getFieldProps('ph_mobile')}
            placeholder='mobile phone'
            type='number'
            onChange={handleInputChange('ph_mobile')}
            iconBg={palette.iconBg}
            error={getError('ph_mobile')}
            leftIcon={
              <Phone
                width={24}
                height={24}
                fill={palette.iconColor}
                opacity={0.6}
              />
            }
          />
          <IconTextField
            autoFocus={autoFocusField === 'postal'}
            pb={{ base: 0.5, md: 1 }}
            {...getFieldProps('postal')}
            placeholder='Zip Code'
            leftIcon={
              <Target
                width={24}
                height={24}
                fill={palette.iconColor}
                opacity={0.6}
              />
            }
            mask={zipCodeMask}
            onChange={handleInputChange('postal')}
            iconBg={palette.iconBg}
            error={getError('postal')}
          />

          <DatePicker
            autoFocus={autoFocusField === 'bday_date'}
            isAuthField
            iconBg={palette.iconBg}
            placeholder='Birthday'
            value={values.bday_date}
            onChange={handleDateChange}
            onTouched={() => setFieldTouched('bday_date')}
            touched={!!touched.bday_date}
            error={getError('bday_date')}
            leftIcon={
              <Cake
                width={24}
                height={22}
                fill={palette.iconColor}
                opacity={0.6}
              />
            }
            onBlur={() => {}}
          />
          {isReferalFlow ? (
            <Flex alignItems='center' w='full'>
              <Center
                borderLeftRadius='6px'
                height='60px'
                w='60px'
                minW='60px'
                bg={palette.iconBg}
              >
                <GenderIcon
                  width={30}
                  height={30}
                  fill={palette.iconColor}
                  opacity={0.6}
                />
              </Center>
              <SelectField
                name='gender'
                setFieldValue={setFieldValue}
                validateField={validateField}
                setFieldTouched={setFieldTouched}
                value={values.gender ?? null}
                options={genderValues}
                error={getError('gender')}
                touched={!!touched.gender}
                placeholder={genderPlaceholder}
                isEmptyStringAllowed
                dropdownIndicatorStyles={{
                  marginRight: '11px',
                  marginTop: '2px'
                }}
                stylesMenu={{
                  background: palette.dropdownBg
                }}
                stylesControl={{
                  fontFamily: fonts.main,
                  fontSize: fontSize || 22,
                  fontWeight: 600,
                  background: palette.dropdownBg,
                  color: palette.dropdownText,
                  height: 60,
                  borderBottomRightRadius: 0,
                  borderBottomLeftRadius: 0,
                  marginBottom: 0,
                  padding: '0 10px'
                }}
                stylesMenuList={{
                  maxHeight: 'auto'
                }}
                stylesPlaceholder={{
                  fontSize: '15px',
                  fontFamily: fonts.main,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  color: palette.placeholder,
                  letterSpacing: 0.5,
                  marginTop: '3px'
                }}
              />
            </Flex>
          ) : (
            <IconTextField
              autoFocus={autoFocusField === 'ref_code'}
              pb={{ base: 0.5, md: 1 }}
              {...getFieldProps('ref_code')}
              name='ref_code'
              value={values.ref_code}
              placeholder='REFERRAL CODE'
              leftIcon={
                <UserGroup
                  width={30}
                  height={30}
                  fill={palette.iconColor}
                  opacity={0.6}
                />
              }
              onChange={handleInputChange('ref_code')}
              iconBg={palette.iconBg}
              error={getError('ref_code')}
              showReset
              sx={{
                caretColor: 'auto',
                color: 'auto'
              }}
            />
          )}
        </SimpleGrid>
        {!isReferalCodeExist && Boolean(campaignsOptions.length) && (
          <Flex alignItems='center' w='full' mt='10px'>
            <Center
              borderLeftRadius='6px'
              height='60px'
              w='60px'
              minW='60px'
              bg={palette.iconBg}
            >
              <CircleQuestion
                width={24}
                height={24}
                fill={palette.iconColor}
                opacity={0.6}
              />
            </Center>
            <SelectField
              name='campaign_id'
              setFieldValue={setFieldValue}
              validateField={validateField}
              setFieldTouched={setFieldTouched}
              value={values.campaign_id}
              options={campaignsOptions}
              error={getError('campaign_id')}
              touched={!!touched.campaign_id}
              placeholder='HOW DID YOU HEAR ABOUT US?'
              dropdownIndicatorStyles={{
                marginRight: '11px',
                marginTop: '2px'
              }}
              stylesMenu={{
                background: palette.dropdownBg,
                marginTop: '-2px'
              }}
              stylesMenuList={{
                height: isTablet ? '150px' : 'auto',
                boxShadow: 'none',
                marginTop: '-2px'
              }}
              stylesControl={{
                fontFamily: fonts.main,
                fontSize: fontSize || 22,
                fontWeight: 600,
                background: palette.dropdownBg,
                color: palette.dropdownText,
                height: 60,
                borderTopRightRadius: 0,
                borderTopLeftRadius: 0,
                marginBottom: 0,
                padding: '0 10px'
              }}
              stylesPlaceholder={{
                fontSize: '15px',
                fontFamily: fonts.main,
                fontWeight: 500,
                textTransform: 'uppercase',
                color: palette.placeholder,
                letterSpacing: 0.5,
                marginTop: '3px'
              }}
              stylesOption={{
                padding: '8px 20px'
              }}
            />
          </Flex>
        )}

        <Stack minH='50px' justifyContent='center'>
          <Text
            position='relative'
            px={2}
            w='full'
            textAlign='center'
            color={palette.text.error}
            bg={palette.text.secondary}
          >
            {errorMsg}
          </Text>
        </Stack>

        {isPolicyAcceptRequired && (
          <Flex
            gap='16px'
            alignItems='flex-start'
            pb='40px'
            justifyContent='center'
          >
            <Checkbox
              variant='outline'
              colorScheme='secondary'
              size='md'
              isChecked={values.policyAccepted}
              onChange={(e) => {
                setFieldValue('policyAccepted', e.target.checked);
                handleClearErrors();
              }}
              isInvalid={!!getError('policyAccepted')}
            >
              <Text fontSize='18px' lineHeight='140%'>
                I have read and agree to the{' '}
                <Link
                  to={config.common?.termsAndConditionsUrl}
                  sx={{
                    fontWeight: '400 !important',
                    textDecoration: 'none',
                    _hover: { textDecoration: 'underline' }
                  }}
                  external
                >
                  Terms and Conditions
                </Link>
              </Text>
            </Checkbox>
          </Flex>
        )}

        <AuthStepButtons
          pb={isPolicyAcceptRequired ? '20px' : 0}
          leftLabel={hasPrefilledData || !isBackButtonEnabled ? '' : 'Back'}
          rightLabel='Next'
          leftHandler={onLoginPage}
          isBtnDisabled={
            isPolicyAcceptRequired ? !values.policyAccepted : false
          }
        />
      </Box>
    </Box>
  );
};

export default AccountForm;
